import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  CalendarRange,
  ChevronDown,
  ClipboardList,
  Clock,
  Copy,
  Layers,
  ListOrdered,
  Percent,
  RefreshCw,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Tags,
  Ticket,
  TrendingUp,
  UserPlus,
  X,
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faCircleCheck,
  faHourglassEnd,
  faSlidersH,
  faTag,
  faTicket,
} from '@fortawesome/free-solid-svg-icons';
import PageHeader from './PageHeader';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';
import Modal from './Modal';
import PersianDatePicker from './PersianDatePicker';
import { BrandSpinnerRing } from './SiteBrandedLoader';
import {
  AdminCoupon,
  AdminCouponRedemption,
  AdminCouponReportDetailed,
  couponsAdminService,
  coursesService,
} from '../services/api';
import type { Course } from '../types';

const formatNum = (n: unknown) =>
  typeof n === 'number' ? n.toLocaleString('fa-IR') : String(n ?? '—');

function toNum(v: unknown): number {
  if (v == null) return NaN;
  if (typeof v === 'number') return v;
  const x = Number(String(v));
  return Number.isFinite(x) ? x : NaN;
}

function formatPaymentStatus(status: string): string {
  const m: Record<string, string> = {
    PAID: 'پرداخت‌شده',
    PENDING: 'در انتظار',
    FAILED: 'ناموفق',
    CANCELLED: 'لغو شده',
  };
  return m[status] ?? status;
}

function formatInvoiceType(t: string): string {
  const m: Record<string, string> = {
    COURSE_PURCHASE: 'خرید دوره',
    WALLET_CHARGE: 'شارژ کیف پول',
    PAYMENT_LINK: 'لینک پرداخت',
  };
  return m[t] ?? t;
}

function userDisplayName(u: AdminCouponRedemption['user'] | undefined): string {
  if (!u) return '—';
  const parts = [u.firstName, u.lastName].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return '—';
}

type CouponListFilters = {
  search: string;
  activeFilter: 'all' | 'active' | 'inactive';
  discountKind: 'all' | 'PERCENT' | 'FIXED_TOMAN';
  campaign: string;
  advancedOnly: boolean;
  usageFilter: 'all' | 'unused' | 'used' | 'nearCap';
  validityFilter: 'all' | 'validNow' | 'expired' | 'expiringSoon';
  createdFrom?: string;
  createdTo?: string;
  sortBy: 'updated' | 'code' | 'usesDesc' | 'endsAsc';
};

const emptyCouponFilters = (): CouponListFilters => ({
  search: '',
  activeFilter: 'all',
  discountKind: 'all',
  campaign: '',
  advancedOnly: false,
  usageFilter: 'all',
  validityFilter: 'all',
  sortBy: 'updated',
});

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeFilterDateValue = (value?: string) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return toDateInputValue(parsed);
};

function couponHasAdvancedRules(c: AdminCoupon): boolean {
  return (
    Boolean(c.firstPaidPurchaseOnly) ||
    (c.minCartLinesCount != null && c.minCartLinesCount >= 1) ||
    (c.minCartSubtotalToman != null && toNum(c.minCartSubtotalToman) > 0) ||
    (c.minOrderToman != null && toNum(c.minOrderToman) > 0) ||
    (c.excludedCourseIds?.length ?? 0) > 0 ||
    (c.applicableCourseIds?.length ?? 0) > 0 ||
    Boolean(c.newUsersRegisteredAfter) ||
    c.maxUsesPerUser != null
  );
}

function matchesCouponFilters(c: AdminCoupon, f: CouponListFilters, nowMs: number): boolean {
  const q = f.search.trim().toLowerCase();
  if (q) {
    const hit =
      c.code.toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q) ||
      (c.campaignLabel || '').toLowerCase().includes(q);
    if (!hit) return false;
  }
  if (f.activeFilter === 'active' && !c.isActive) return false;
  if (f.activeFilter === 'inactive' && c.isActive) return false;
  if (f.discountKind !== 'all' && c.discountKind !== f.discountKind) return false;
  if (f.campaign.trim() && (c.campaignLabel || '').trim() !== f.campaign.trim()) return false;
  if (f.advancedOnly && !couponHasAdvancedRules(c)) return false;

  if (f.usageFilter === 'unused' && c.usesCount > 0) return false;
  if (f.usageFilter === 'used' && c.usesCount === 0) return false;
  if (f.usageFilter === 'nearCap') {
    if (c.maxUsesTotal == null || c.maxUsesTotal <= 0) return false;
    const threshold = Math.max(1, Math.floor(Number(c.maxUsesTotal) * 0.85));
    if (c.usesCount < threshold) return false;
  }

  const startMs = c.startsAt ? new Date(c.startsAt).getTime() : null;
  const endMs = c.endsAt ? new Date(c.endsAt).getTime() : null;

  if (f.validityFilter === 'expired') {
    if (endMs == null || endMs >= nowMs) return false;
  }
  if (f.validityFilter === 'validNow') {
    if (!c.isActive) return false;
    if (startMs != null && startMs > nowMs) return false;
    if (endMs != null && endMs < nowMs) return false;
  }
  if (f.validityFilter === 'expiringSoon') {
    if (endMs == null) return false;
    const week = 7 * 24 * 60 * 60 * 1000;
    if (endMs < nowMs || endMs > nowMs + week) return false;
  }

  const fromNorm = normalizeFilterDateValue(f.createdFrom);
  const toNorm = normalizeFilterDateValue(f.createdTo);
  if (fromNorm || toNorm) {
    const created = new Date(c.createdAt).getTime();
    if (fromNorm) {
      const fromT = new Date(`${fromNorm}T00:00:00`).getTime();
      if (created < fromT) return false;
    }
    if (toNorm) {
      const toT = new Date(`${toNorm}T23:59:59.999`).getTime();
      if (created > toT) return false;
    }
  }

  return true;
}

function sortCouponsList(arr: AdminCoupon[], sortBy: CouponListFilters['sortBy']): AdminCoupon[] {
  const copy = [...arr];
  const cmpStr = (a: string, b: string) => a.localeCompare(b, 'fa', { sensitivity: 'base' });
  copy.sort((a, b) => {
    if (sortBy === 'code') return cmpStr(a.code, b.code);
    if (sortBy === 'usesDesc') return b.usesCount - a.usesCount || cmpStr(a.code, b.code);
    if (sortBy === 'endsAsc') {
      const ae = a.endsAt ? new Date(a.endsAt).getTime() : Infinity;
      const be = b.endsAt ? new Date(b.endsAt).getTime() : Infinity;
      return ae - be || cmpStr(a.code, b.code);
    }
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime() || cmpStr(a.code, b.code);
  });
  return copy;
}

type FormState = {
  code: string;
  description: string;
  discountKind: 'PERCENT' | 'FIXED_TOMAN';
  percentOff: string;
  amountOffToman: string;
  maxUsesTotal: string;
  perUserPerCourseLimit: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  applicableIds: string[];
  excludedIds: string[];
  minOrderToman: string;
  maxDiscountToman: string;
  maxUsesPerUser: string;
  firstPaidPurchaseOnly: boolean;
  minCartLinesCount: string;
  minCartSubtotalToman: string;
  newUsersRegisteredAfter: string;
  campaignLabel: string;
};

const emptyForm = (): FormState => ({
  code: '',
  description: '',
  discountKind: 'PERCENT',
  percentOff: '10',
  amountOffToman: '',
  maxUsesTotal: '',
  perUserPerCourseLimit: '1',
  startsAt: '',
  endsAt: '',
  isActive: true,
  applicableIds: [],
  excludedIds: [],
  minOrderToman: '',
  maxDiscountToman: '',
  maxUsesPerUser: '1',
  firstPaidPurchaseOnly: false,
  minCartLinesCount: '',
  minCartSubtotalToman: '',
  newUsersRegisteredAfter: '',
  campaignLabel: '',
});

function CourseIdListPicker({
  title,
  hint,
  listId,
  selectedIds,
  counterpartIds,
  courses,
  onChange,
}: {
  title: string;
  hint: string;
  listId: string;
  selectedIds: string[];
  counterpartIds: string[];
  courses: Course[];
  onChange: (next: string[]) => void;
}) {
  const [draftId, setDraftId] = useState('');
  const [open, setOpen] = useState(true);

  const courseById = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses]);

  const availableOptions = useMemo(() => {
    const block = new Set([...counterpartIds]);
    return courses.filter((c) => !selectedIds.includes(c.id) && !block.has(c.id));
  }, [courses, counterpartIds, selectedIds]);

  const filteredSelect = useMemo(() => {
    const q = draftId.trim().toLowerCase();
    if (!q) return availableOptions;
    return availableOptions.filter(
      (c) =>
        c.id.toLowerCase().includes(q) ||
        (c.title || '').toLowerCase().includes(q) ||
        (c.slug || '').toLowerCase().includes(q),
    );
  }, [availableOptions, draftId]);

  const addId = (id: string) => {
    const t = id.trim();
    if (!t || selectedIds.includes(t)) return;
    onChange([...selectedIds, t]);
    setDraftId('');
  };

  const removeAt = (id: string) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 space-y-2">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-right"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-xs font-black text-slate-800">{title}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-500 transition ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <>
          <p className="text-[11px] font-semibold leading-relaxed text-slate-600">{hint}</p>
          <div className="flex flex-wrap gap-2">
            <input
              dir="ltr"
              list={listId}
              className="min-w-[180px] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs"
              placeholder="جستجو شناسه / عنوان دوره…"
              value={draftId}
              onChange={(e) => setDraftId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const hit = filteredSelect.find(
                    (c) =>
                      c.id === draftId.trim() ||
                      (c.slug && c.slug === draftId.trim()),
                  );
                  addId(hit ? hit.id : draftId);
                }
              }}
            />
            <datalist id={listId}>
              {filteredSelect.slice(0, 35).map((c) => (
                <option key={c.id} value={c.id}>
                  {(c.title || '').slice(0, 72)}
                </option>
              ))}
            </datalist>
            <select
              className="min-w-[200px] flex-1 rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold"
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (v) addId(v);
              }}
            >
              <option value="">انتخاب از لیست…</option>
              {filteredSelect.slice(0, 120).map((c) => (
                <option key={c.id} value={c.id}>
                  {(c.title || 'بدون عنوان').slice(0, 52)} ({c.id.slice(0, 8)}…)
                </option>
              ))}
            </select>
            <button
              type="button"
              className="rounded-lg bg-slate-800 px-3 py-2 text-[11px] font-black text-white hover:bg-slate-900"
              onClick={() => addId(draftId)}
            >
              افزودن
            </button>
          </div>
          <ul className="max-h-40 space-y-1.5 overflow-y-auto rounded-xl border border-slate-100 bg-white p-2">
            {selectedIds.length === 0 ? (
              <li className="py-6 text-center text-[11px] font-bold text-slate-400">هنوز دوره‌ای اضافه نشده.</li>
            ) : (
              selectedIds.map((id) => {
                const meta = courseById.get(id);
                return (
                  <li
                    key={id}
                    className="flex items-start justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/70 px-2 py-1.5"
                  >
                    <div className="min-w-0">
                      <p className="text-[11px] font-black text-slate-900 truncate">
                        {meta?.title || 'شناسه ناشناس'}
                      </p>
                      <p className="mt-0.5 font-mono text-[10px] text-slate-500 truncate" dir="ltr">
                        {id}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 rounded-lg border border-red-100 bg-white p-1 text-red-600 hover:bg-red-50"
                      onClick={() => removeAt(id)}
                      aria-label="حذف"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </>
      ) : null}
    </section>
  );
}

function SectionCard({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_1px_0_rgba(15,23,42,0.04)] space-y-3">
      <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600/10 to-blue-600/5 text-blue-700">
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-sm font-black text-slate-900">{title}</span>
      </div>
      {children}
    </div>
  );
}

function downloadCouponReportCsv(d: AdminCouponReportDetailed) {
  const bom = '\uFEFF';
  const rows: string[][] = [
    ['کد', 'کمپین', 'فعال', 'نوع تخفیف', 'کل استفاده', 'حداکثر کل', 'بازخرید در بازه', 'مجموع تخفیف در بازه'],
  ];
  for (const c of d.couponMatrix) {
    rows.push([
      c.code,
      c.campaignLabel ?? '',
      c.isActive ? 'بله' : 'خیر',
      c.discountKind === 'PERCENT' ? 'درصد' : 'ثابت',
      String(c.usesCountLifetime),
      c.maxUsesTotal != null ? String(c.maxUsesTotal) : '',
      String(c.windowRedemptions),
      String(c.windowDiscountTomanSum),
    ]);
  }
  const body = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([bom + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `coupon-report-${d.days}d-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadDailyCsv(d: AdminCouponReportDetailed) {
  const bom = '\uFEFF';
  const rows: string[][] = [['تاریخ', 'تعداد بازخرید', 'جمع تخفیف تومان']];
  for (const row of d.dailyTrend) {
    rows.push([row.date, String(row.redemptionCount), String(row.discountTomanSum)]);
  }
  const body = rows.map((r) => r.map((cell) => `"${cell}"`).join(',')).join('\n');
  const blob = new Blob([bom + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `coupon-daily-${d.days}d.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const Coupons: React.FC = () => {
  const [tab, setTab] = useState<'list' | 'report'>('list');
  const [rows, setRows] = useState<AdminCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AdminCoupon | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [listFilters, setListFilters] = useState<CouponListFilters>(() => emptyCouponFilters());
  const [listPage, setListPage] = useState(1);
  const listPageSize = 12;
  const [copiedCouponId, setCopiedCouponId] = useState<string | null>(null);
  const [redemptionsCoupon, setRedemptionsCoupon] = useState<AdminCoupon | null>(null);
  const [redemptionRows, setRedemptionRows] = useState<AdminCouponRedemption[]>([]);
  const [redemptionLoading, setRedemptionLoading] = useState(false);
  const [coursesCatalog, setCoursesCatalog] = useState<Course[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);

  const [reportDays, setReportDays] = useState(30);
  const [reportData, setReportData] = useState<AdminCouponReportDetailed | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      try {
        await couponsAdminService.reconcileRedemptionsFromInvoices(10000);
      } catch {
        /* گزارش از فاکتور هم‌رسان می‌شود؛ شکست این مرحله نباید لیست را خالی کند */
      }
      const data = await couponsAdminService.list();
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'خطا در دریافت کوپن‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!modalOpen) return;
    let alive = true;
    void (async () => {
      setCoursesLoading(true);
      try {
        let list = await coursesService.getPublished();
        if (!alive) return;
        if (!Array.isArray(list) || list.length === 0) {
          list = await coursesService.getAll();
        }
        if (!alive) return;
        setCoursesCatalog(Array.isArray(list) ? list : []);
      } catch {
        if (!alive) return;
        setCoursesCatalog([]);
      } finally {
        if (alive) setCoursesLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [modalOpen]);

  const refreshReport = useCallback(async () => {
    setReportLoading(true);
    setReportError('');
    try {
      const data = await couponsAdminService.reportDetailed(reportDays);
      setReportData(data);
    } catch (e: any) {
      setReportError(e?.response?.data?.message || 'خطا در گزارش');
      setReportData(null);
    } finally {
      setReportLoading(false);
    }
  }, [reportDays]);

  useEffect(() => {
    if (tab !== 'report') return;
    void refreshReport();
  }, [tab, refreshReport]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (c: AdminCoupon) => {
    setEditing(c);
    setForm({
      code: c.code,
      description: c.description || '',
      discountKind: c.discountKind,
      percentOff: c.percentOff != null ? String(toNum(c.percentOff)) : '',
      amountOffToman: c.amountOffToman != null ? String(toNum(c.amountOffToman)) : '',
      maxUsesTotal: c.maxUsesTotal != null ? String(c.maxUsesTotal) : '',
      perUserPerCourseLimit: String(c.perUserPerCourseLimit ?? 1),
      startsAt: c.startsAt ? c.startsAt.slice(0, 16) : '',
      endsAt: c.endsAt ? c.endsAt.slice(0, 16) : '',
      isActive: c.isActive,
      applicableIds: [...(c.applicableCourseIds || [])],
      excludedIds: [...(c.excludedCourseIds || [])],
      minOrderToman: c.minOrderToman != null ? String(toNum(c.minOrderToman)) : '',
      maxDiscountToman: c.maxDiscountToman != null ? String(toNum(c.maxDiscountToman)) : '',
      maxUsesPerUser: c.maxUsesPerUser != null ? String(c.maxUsesPerUser) : '',
      firstPaidPurchaseOnly: !!c.firstPaidPurchaseOnly,
      minCartLinesCount: c.minCartLinesCount != null ? String(c.minCartLinesCount) : '',
      minCartSubtotalToman: c.minCartSubtotalToman != null ? String(toNum(c.minCartSubtotalToman)) : '',
      newUsersRegisteredAfter: c.newUsersRegisteredAfter ? c.newUsersRegisteredAfter.slice(0, 16) : '',
      campaignLabel: c.campaignLabel || '',
    });
    setModalOpen(true);
  };

  const filteredSortedRows = useMemo(() => {
    const nowMs = Date.now();
    const matched = rows.filter((c) => matchesCouponFilters(c, listFilters, nowMs));
    return sortCouponsList(matched, listFilters.sortBy);
  }, [rows, listFilters]);

  useEffect(() => {
    setListPage(1);
  }, [listFilters]);

  const listTotalPages = Math.max(1, Math.ceil(filteredSortedRows.length / listPageSize));
  const listPageSafe = Math.min(listPage, listTotalPages);
  const paginatedRows = useMemo(() => {
    const start = (listPageSafe - 1) * listPageSize;
    return filteredSortedRows.slice(start, start + listPageSize);
  }, [filteredSortedRows, listPageSafe]);

  const activeListFilterCount = useMemo(() => {
    const f = listFilters;
    return [
      f.search.trim(),
      f.activeFilter !== 'all',
      f.discountKind !== 'all',
      f.campaign.trim(),
      f.advancedOnly,
      f.usageFilter !== 'all',
      f.validityFilter !== 'all',
      f.createdFrom,
      f.createdTo,
    ].filter(Boolean).length;
  }, [listFilters]);

  const couponStats = useMemo(() => {
    const now = Date.now();
    const week = 7 * 24 * 60 * 60 * 1000;
    return {
      total: rows.length,
      active: rows.filter((r) => r.isActive).length,
      inactive: rows.filter((r) => !r.isActive).length,
      percent: rows.filter((r) => r.discountKind === 'PERCENT').length,
      fixed: rows.filter((r) => r.discountKind === 'FIXED_TOMAN').length,
      used: rows.filter((r) => r.usesCount > 0).length,
      withAdvanced: rows.filter((c) => couponHasAdvancedRules(c)).length,
      expiringSoon: rows.filter((r) => {
        const e = r.endsAt ? new Date(r.endsAt).getTime() : null;
        return e != null && e >= now && e <= now + week;
      }).length,
      expired: rows.filter((r) => {
        const e = r.endsAt ? new Date(r.endsAt).getTime() : null;
        return e != null && e < now;
      }).length,
    };
  }, [rows]);

  const campaignOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const t = (r.campaignLabel || '').trim();
      if (t) set.add(t);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fa'));
  }, [rows]);

  const copyCouponCode = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCouponId(id);
      window.setTimeout(() => setCopiedCouponId(null), 1200);
    } catch {
      // noop
    }
  };

  const openRedemptions = async (c: AdminCoupon) => {
    setRedemptionsCoupon(c);
    setRedemptionLoading(true);
    setRedemptionRows([]);
    try {
      const data = await couponsAdminService.redemptions(c.id);
      setRedemptionRows(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      alert(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'خطا در دریافت گزارش استفاده',
      );
      setRedemptionsCoupon(null);
    } finally {
      setRedemptionLoading(false);
    }
  };

  const handleToggleActive = async (c: AdminCoupon) => {
    try {
      await couponsAdminService.update(c.id, { isActive: !c.isActive });
      await load();
    } catch (err: unknown) {
      alert(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'خطا در به‌روزرسانی',
      );
    }
  };

  const handleDuplicate = async (c: AdminCoupon) => {
    const custom = window.prompt('کد کوپن جدید (خالی = خودکار با پسوند _COPY):', '');
    if (custom === null) return;
    try {
      await couponsAdminService.duplicate(c.id, custom.trim() ? { code: custom.trim() } : undefined);
      await load();
    } catch (err: unknown) {
      alert(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'کپی ناموفق بود',
      );
    }
  };

  const buildPayload = (): Record<string, unknown> => {
    const ids = [...form.applicableIds];
    let excluded = [...form.excludedIds];
    excluded = excluded.filter((x) => !ids.includes(x));
    const payload: Record<string, unknown> = {
      code: form.code.trim(),
      description: form.description.trim() || undefined,
      discountKind: form.discountKind,
      maxUsesTotal: form.maxUsesTotal.trim() ? Number(form.maxUsesTotal.trim()) : undefined,
      perUserPerCourseLimit: form.perUserPerCourseLimit.trim()
        ? Number(form.perUserPerCourseLimit.trim())
        : 1,
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : undefined,
      endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : undefined,
      isActive: form.isActive,
      applicableCourseIds: ids,
      excludedCourseIds: excluded,
      minCartLinesCount: form.minCartLinesCount.trim() ? Number(form.minCartLinesCount.trim()) : undefined,
      minCartSubtotalToman: form.minCartSubtotalToman.trim()
        ? Number(form.minCartSubtotalToman.trim())
        : undefined,
      newUsersRegisteredAfter: form.newUsersRegisteredAfter
        ? new Date(form.newUsersRegisteredAfter).toISOString()
        : undefined,
      campaignLabel: form.campaignLabel.trim() || undefined,
      minOrderToman: form.minOrderToman.trim() ? Number(form.minOrderToman.trim()) : undefined,
      maxUsesPerUser: form.maxUsesPerUser.trim() ? Number(form.maxUsesPerUser.trim()) : undefined,
      firstPaidPurchaseOnly: form.firstPaidPurchaseOnly,
    };
    if (form.discountKind === 'PERCENT') {
      payload.percentOff = Number(form.percentOff.trim()) || undefined;
      payload.amountOffToman = undefined;
      payload.maxDiscountToman = form.maxDiscountToman.trim() ? Number(form.maxDiscountToman.trim()) : undefined;
    } else {
      payload.amountOffToman = Number(form.amountOffToman.trim()) || undefined;
      payload.percentOff = undefined;
      payload.maxDiscountToman = undefined;
    }
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = buildPayload();
      if (editing) {
        await couponsAdminService.update(editing.id, payload);
      } else {
        await couponsAdminService.create(payload);
      }
      setModalOpen(false);
      await load();
      if (tab === 'report') void refreshReport();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'ذخیره ناموفق بود');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (c: AdminCoupon) => {
    if (!window.confirm(`حذف کوپن «${c.code}»؟`)) return;
    try {
      await couponsAdminService.remove(c.id);
      await load();
      if (tab === 'report') void refreshReport();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'خطا در حذف');
    }
  };

  const summary = useMemo(() => {
    if (!listFilters.search.trim() && activeListFilterCount === 0) {
      return `${rows.length} کوپن در سیستم`;
    }
    return `${filteredSortedRows.length.toLocaleString('fa-IR')} مورد از ${rows.length.toLocaleString('fa-IR')} کوپن (با فیلتر)`;
  }, [rows.length, filteredSortedRows.length, listFilters.search, activeListFilterCount]);

  if (loading && rows.length === 0) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1280px] mx-auto rtl text-right space-y-4">
      <PageHeader title="مدیریت کوپن و گزارش" subtitle={tab === 'list' ? summary : 'تحلیل بازخرید و عملکرد کمپین‌ها'} />

      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
        <button
          type="button"
          onClick={() => setTab('list')}
          className={`rounded-xl px-4 py-2 text-sm font-black transition ${
            tab === 'list' ? 'bg-blue-600 text-white shadow' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          لیست کوپن‌ها
        </button>
        <button
          type="button"
          onClick={() => setTab('report')}
          className={`rounded-xl px-4 py-2 text-sm font-black transition inline-flex items-center gap-2 ${
            tab === 'report' ? 'bg-blue-600 text-white shadow' : 'text-slate-600 hover:bg-slate-50'
          }`}
        >
          <BarChart3 className="h-4 w-4" />
          گزارش حرفه‌ای
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div>
      ) : null}

      {tab === 'report' ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-black text-slate-600">بازه زمانی:</span>
              {[7, 30, 90, 180].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setReportDays(d)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-black ${
                    reportDays === d ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {d} روز
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void refreshReport()}
                disabled={reportLoading}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-black text-slate-800 hover:bg-slate-50 disabled:opacity-50"
              >
                بروزرسانی
              </button>
              {reportData ? (
                <>
                  <button
                    type="button"
                    onClick={() => downloadCouponReportCsv(reportData)}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-black text-white hover:bg-emerald-700"
                  >
                    CSV ماتریس کوپن
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadDailyCsv(reportData)}
                    className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-black text-white hover:bg-slate-900"
                  >
                    CSV روند روزانه
                  </button>
                </>
              ) : null}
            </div>
          </div>

          {reportError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm">{reportError}</div>
          ) : null}

          {reportLoading && !reportData ? (
            <div className="py-16 flex justify-center">
              <LoadingSpinner />
            </div>
          ) : reportData ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2">
                {[
                  { label: `بازخرید در بازه (${reportData.days}روز)`, value: formatNum(reportData.windowRedemptions) },
                  { label: `تخفیف در بازه (ت)`, value: formatNum(reportData.windowDiscountTomanSum) },
                  { label: 'میانگین تخفیف / بازخرید', value: `${formatNum(reportData.avgDiscountPerRedemptionWindow)} ت` },
                  { label: 'بازخرید (کل систем)', value: formatNum(reportData.lifetimeRedemptions) },
                  { label: 'تخفیف کل (ت)', value: formatNum(reportData.lifetimeDiscountTomanSum) },
                  { label: 'کوپن فعال / کل', value: `${reportData.couponsActive}/${reportData.couponsConfigured}` },
                ].map((c) => (
                  <div key={c.label} className="rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
                    <p className="text-[10px] font-bold text-slate-500">{c.label}</p>
                    <p className="mt-1 text-sm font-black text-slate-900">{c.value}</p>
                  </div>
                ))}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-black text-slate-900 mb-2">برترین بر اساس تعداد بازخرید</h3>
                  <div className="overflow-x-auto max-h-[320px] overflow-y-auto rounded-xl border border-slate-100">
                    <table className="min-w-full text-xs">
                      <thead className="sticky top-0 bg-slate-50 font-black text-slate-600">
                        <tr>
                          <th className="px-2 py-2 text-right">کد</th>
                          <th className="px-2 py-2 text-right">کمپین</th>
                          <th className="px-2 py-2 text-right">تعداد</th>
                          <th className="px-2 py-2 text-right">جمع تخفیف</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.topByRedemptions.slice(0, 20).map((row) => (
                          <tr key={row.couponId} className="border-t border-slate-100">
                            <td className="px-2 py-1 font-mono" dir="ltr">
                              {row.code}
                            </td>
                            <td className="px-2 py-1">{row.campaignLabel || '—'}</td>
                            <td className="px-2 py-1">{formatNum(row.redemptionsCount)}</td>
                            <td className="px-2 py-1">{formatNum(row.discountTomanSum)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-black text-slate-900 mb-2">برترین بر اساس مبلغ تخفیف</h3>
                  <div className="overflow-x-auto max-h-[320px] overflow-y-auto rounded-xl border border-slate-100">
                    <table className="min-w-full text-xs">
                      <thead className="sticky top-0 bg-slate-50 font-black text-slate-600">
                        <tr>
                          <th className="px-2 py-2 text-right">کد</th>
                          <th className="px-2 py-2 text-right">کمپین</th>
                          <th className="px-2 py-2 text-right">جمع تخفیف</th>
                          <th className="px-2 py-2 text-right">تعداد</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.topByDiscountSum.slice(0, 20).map((row) => (
                          <tr key={row.couponId} className="border-t border-slate-100">
                            <td className="px-2 py-1 font-mono" dir="ltr">
                              {row.code}
                            </td>
                            <td className="px-2 py-1">{row.campaignLabel || '—'}</td>
                            <td className="px-2 py-1 font-black">{formatNum(row.discountTomanSum)}</td>
                            <td className="px-2 py-1">{formatNum(row.redemptionsCount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-black text-slate-900 mb-2">جمع بازخرید بر اساس برچسب کمپین</h3>
                <div className="overflow-x-auto rounded-xl border border-slate-100 max-h-[220px] overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 font-black text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-right">کمپین</th>
                        <th className="px-3 py-2 text-right">بازخرید</th>
                        <th className="px-3 py-2 text-right">جمع تخفیف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.byCampaign.map((row, idx) => (
                        <tr key={`${row.campaignLabel}-${idx}`} className="border-t border-slate-100">
                          <td className="px-3 py-2">{row.campaignLabel || 'بدون برچسب کمپین'}</td>
                          <td className="px-3 py-2">{formatNum(row.redemptionsCount)}</td>
                          <td className="px-3 py-2">{formatNum(row.discountTomanSum)}</td>
                        </tr>
                      ))}
                      {reportData.byCampaign.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-3 py-6 text-center text-slate-500">
                            داده‌ای در این بازه نیست.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <h3 className="text-sm font-black text-slate-900">روند روزانه بازخرید</h3>
                  <span className="text-[11px] font-bold text-slate-500">هر سلول؛ همان تاریخ محلی سرور</span>
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-100 max-h-[280px] overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 font-black text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-right">تاریخ</th>
                        <th className="px-3 py-2 text-right">بازخرید</th>
                        <th className="px-3 py-2 text-right">تخفیف (تومان)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...reportData.dailyTrend]
                        .filter((row) => row.redemptionCount > 0 || row.discountTomanSum > 0)
                        .slice()
                        .reverse()
                        .map((row) => (
                          <tr key={row.date} className="border-t border-slate-100">
                            <td className="px-3 py-1 font-mono" dir="ltr">
                              {row.date}
                            </td>
                            <td className="px-3 py-1">{formatNum(row.redemptionCount)}</td>
                            <td className="px-3 py-1">{formatNum(row.discountTomanSum)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm overflow-hidden">
                <h3 className="text-sm font-black text-slate-900 mb-2">ماتریس همهٔ کوپن‌ها (در بازه انتخابی)</h3>
                <div className="overflow-x-auto max-h-[420px] overflow-y-auto rounded-xl border border-slate-100">
                  <table className="min-w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 font-black text-slate-600 z-10">
                      <tr>
                        <th className="px-2 py-2 text-right">کد</th>
                        <th className="px-2 py-2 text-right">کمپین</th>
                        <th className="px-2 py-2 text-right">وضعیت</th>
                        <th className="px-2 py-2 text-right">بازخرید بازه</th>
                        <th className="px-2 py-2 text-right">تخفیف بازه</th>
                        <th className="px-2 py-2 text-right">کل مصرف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.couponMatrix.map((row) => (
                        <tr key={row.couponId} className="border-t border-slate-100 hover:bg-slate-50/80">
                          <td className="px-2 py-1 font-mono" dir="ltr">
                            {row.code}
                          </td>
                          <td className="px-2 py-1">{row.campaignLabel || '—'}</td>
                          <td className="px-2 py-1">{row.isActive ? 'فعال' : 'غیرفعال'}</td>
                          <td className="px-2 py-1">{formatNum(row.windowRedemptions)}</td>
                          <td className="px-2 py-1">{formatNum(row.windowDiscountTomanSum)}</td>
                          <td className="px-2 py-1">{formatNum(row.usesCountLifetime)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-black text-gray-900 sm:text-xl">نمای کارتی کوپن‌ها</h2>
                <p className="mt-1 text-sm font-medium text-gray-600">
                  فیلترهای ترکیبی، مرتب‌سازی و شبیه داشبورد لیدها؛ هر کارت یک کوپن با وضعیت و مصرف یک‌نگر.
                </p>
              </div>
              <button
                type="button"
                onClick={openCreate}
                className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-black text-white shadow-sm shadow-indigo-500/25 transition hover:bg-indigo-700"
              >
                کوپن جدید
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
            {[
              {
                label: 'کل کوپن‌ها',
                val: couponStats.total,
                border: 'border-blue-100',
                grad: 'from-blue-50 to-white',
                lbl: 'text-blue-700',
                num: 'text-blue-900',
                wrap: 'bg-blue-100 text-blue-700',
                icon: faTicket,
              },
              {
                label: 'فعال',
                val: couponStats.active,
                border: 'border-emerald-100',
                grad: 'from-emerald-50 to-white',
                lbl: 'text-emerald-700',
                num: 'text-emerald-900',
                wrap: 'bg-emerald-100 text-emerald-700',
                icon: faCircleCheck,
              },
              {
                label: 'غیرفعال',
                val: couponStats.inactive,
                border: 'border-slate-200',
                grad: 'from-slate-50 to-white',
                lbl: 'text-slate-700',
                num: 'text-slate-900',
                wrap: 'bg-slate-200 text-slate-700',
                icon: faTag,
              },
              {
                label: 'دارای بازخرید',
                val: couponStats.used,
                border: 'border-violet-100',
                grad: 'from-violet-50 to-white',
                lbl: 'text-violet-700',
                num: 'text-violet-900',
                wrap: 'bg-violet-100 text-violet-700',
                icon: faBolt,
              },
              {
                label: 'قوانین پیشرفته',
                val: couponStats.withAdvanced,
                border: 'border-amber-100',
                grad: 'from-amber-50 to-white',
                lbl: 'text-amber-800',
                num: 'text-amber-900',
                wrap: 'bg-amber-100 text-amber-800',
                icon: faSlidersH,
              },
              {
                label: 'منقضی در ۷ روز',
                val: couponStats.expiringSoon,
                border: 'border-orange-100',
                grad: 'from-orange-50 to-white',
                lbl: 'text-orange-700',
                num: 'text-orange-900',
                wrap: 'bg-orange-100 text-orange-700',
                icon: faHourglassEnd,
              },
            ].map((card) => (
              <div
                key={card.label}
                className={`rounded-2xl border bg-gradient-to-b p-4 shadow-sm ${card.border} ${card.grad}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className={`text-xs font-bold ${card.lbl}`}>{card.label}</p>
                    <p className={`mt-2 text-2xl font-black ${card.num}`}>{card.val.toLocaleString('fa-IR')}</p>
                  </div>
                  <span
                    className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${card.wrap}`}
                  >
                    <FontAwesomeIcon icon={card.icon} className="h-4 w-4" />
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 rounded-2xl bg-white p-4 shadow-sm sm:p-5 xl:grid-cols-6">
            <div className="xl:col-span-6 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-black text-slate-700">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                میانبر
              </span>
              <button
                type="button"
                onClick={() =>
                  setListFilters((prev) => ({ ...prev, activeFilter: 'active', validityFilter: 'validNow', usageFilter: 'all' }))
                }
                className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800 hover:bg-emerald-100"
              >
                فعال و در بازهٔ اعتبار
              </button>
              <button
                type="button"
                onClick={() => setListFilters((prev) => ({ ...prev, usageFilter: 'used' }))}
                className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-bold text-violet-800 hover:bg-violet-100"
              >
                حداقل یک بازخرید
              </button>
              <button
                type="button"
                onClick={() => setListFilters((prev) => ({ ...prev, usageFilter: 'nearCap' }))}
                className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-bold text-rose-800 hover:bg-rose-100"
              >
                نزدیک سقف کل مصرف
              </button>
              <button
                type="button"
                onClick={() => setListFilters((prev) => ({ ...prev, validityFilter: 'expiringSoon', activeFilter: 'active' }))}
                className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800 hover:bg-amber-100"
              >
                به‌زودی منقضی
              </button>
              <button
                type="button"
                onClick={() => setListFilters((prev) => ({ ...prev, advancedOnly: true }))}
                className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-800 hover:bg-indigo-100"
              >
                فقط قوانین پیشرفته
              </button>
              {activeListFilterCount > 0 ? (
                <span className="rounded-full bg-slate-900/5 px-2.5 py-1 text-[11px] font-black text-slate-600">
                  {activeListFilterCount.toLocaleString('fa-IR')} فیلتر فعال
                </span>
              ) : null}
            </div>

            <input
              type="search"
              className="sm:col-span-2 xl:col-span-2 rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-semibold"
              placeholder="جستجو در کد، توضیح، کمپین…"
              value={listFilters.search}
              onChange={(e) => setListFilters((p) => ({ ...p, search: e.target.value }))}
            />
            <select
              className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
              value={listFilters.activeFilter}
              onChange={(e) =>
                setListFilters((p) => ({ ...p, activeFilter: e.target.value as CouponListFilters['activeFilter'] }))
              }
            >
              <option value="all">وضعیت: همه</option>
              <option value="active">فعال</option>
              <option value="inactive">غیرفعال</option>
            </select>
            <select
              className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
              value={listFilters.discountKind}
              onChange={(e) =>
                setListFilters((p) => ({ ...p, discountKind: e.target.value as CouponListFilters['discountKind'] }))
              }
            >
              <option value="all">نوع تخفیف: همه</option>
              <option value="PERCENT">درصدی</option>
              <option value="FIXED_TOMAN">مبلغ ثابت</option>
            </select>
            <select
              className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
              value={listFilters.usageFilter}
              onChange={(e) =>
                setListFilters((p) => ({ ...p, usageFilter: e.target.value as CouponListFilters['usageFilter'] }))
              }
            >
              <option value="all">مصرف: همه</option>
              <option value="unused">بدون بازخرید</option>
              <option value="used">دارای بازخرید</option>
              <option value="nearCap">نزدیک سقف کل</option>
            </select>
            <select
              className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
              value={listFilters.validityFilter}
              onChange={(e) =>
                setListFilters((p) => ({ ...p, validityFilter: e.target.value as CouponListFilters['validityFilter'] }))
              }
            >
              <option value="all">اعتبار زمانی: همه</option>
              <option value="validNow">الان معتبر (فعال + بازه)</option>
              <option value="expired">منقضی‌شده</option>
              <option value="expiringSoon">۷ روز آینده</option>
            </select>

            <select
              className="sm:col-span-2 xl:col-span-2 rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
              value={listFilters.campaign}
              onChange={(e) => setListFilters((p) => ({ ...p, campaign: e.target.value }))}
            >
              <option value="">کمپین: همه</option>
              {campaignOptions.map((lab) => (
                <option key={lab} value={lab}>
                  {lab}
                </option>
              ))}
            </select>

            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={listFilters.advancedOnly}
                onChange={(e) => setListFilters((p) => ({ ...p, advancedOnly: e.target.checked }))}
              />
              <span className="font-bold text-slate-700">فقط کوپن‌های با قواعد پیشرفته</span>
            </label>

            <PersianDatePicker
              value={listFilters.createdFrom || ''}
              onChange={(value) =>
                setListFilters((prev) => ({
                  ...prev,
                  createdFrom: normalizeFilterDateValue(value),
                }))
              }
              placeholder="ساخته‌شده از (شمسی)"
              className="min-w-[12rem]"
            />
            <PersianDatePicker
              value={listFilters.createdTo || ''}
              onChange={(value) =>
                setListFilters((prev) => ({
                  ...prev,
                  createdTo: normalizeFilterDateValue(value),
                }))
              }
              placeholder="ساخته‌شده تا (شمسی)"
              className="min-w-[12rem]"
            />

            <select
              className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm xl:col-span-2"
              value={listFilters.sortBy}
              onChange={(e) =>
                setListFilters((p) => ({ ...p, sortBy: e.target.value as CouponListFilters['sortBy'] }))
              }
            >
              <option value="updated">مرتب‌سازی: آخرین بروزرسانی</option>
              <option value="code">مرتب‌سازی: کد الفبایی</option>
              <option value="usesDesc">مرتب‌سازی: بیشترین مصرف</option>
              <option value="endsAsc">مرتب‌سازی: نزدیک‌ترین پایان</option>
            </select>

            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {loading ? <BrandSpinnerRing size="sm" /> : <RefreshCw className="h-4 w-4" />}
              بروزرسانی لیست
            </button>
            <button
              type="button"
              onClick={() => {
                setListFilters(emptyCouponFilters());
              }}
              className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50"
            >
              پاک‌کردن فیلترها
            </button>
          </div>

          {rows.length === 0 ? (
            <EmptyState
              icon={<Ticket className="mx-auto h-10 w-10" />}
              title="هنوز کوپنی ثبت نشده"
              description="برای ساخت اولین کوپن، دکمهٔ «کوپن جدید» را بزنید."
            />
          ) : filteredSortedRows.length === 0 ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-6 py-10 text-center">
              <p className="text-base font-black text-amber-900">با فیلترهای فعلی کوپنی پیدا نشد.</p>
              <button
                type="button"
                onClick={() => setListFilters(emptyCouponFilters())}
                className="mt-4 rounded-xl bg-white px-4 py-2 text-sm font-bold text-amber-900 shadow hover:bg-amber-100/80"
              >
                حذف فیلترها
              </button>
            </div>
          ) : (
            <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-indigo-700">
                  <BrandSpinnerRing size="sm" />
                  <span className="text-sm font-bold">به‌روزرسانی...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {paginatedRows.map((c) => {
                    const now = Date.now();
                    const endMs = c.endsAt ? new Date(c.endsAt).getTime() : null;
                    const startMs = c.startsAt ? new Date(c.startsAt).getTime() : null;
                    const expired = endMs != null && endMs < now;
                    const expiringSoon = endMs != null && endMs >= now && endMs <= now + 7 * 24 * 60 * 60 * 1000;
                    let accent =
                      'from-emerald-500 via-teal-500 to-cyan-600 shadow-emerald-500/25';
                    if (!c.isActive) accent = 'from-slate-400 to-slate-600 shadow-slate-400/15';
                    else if (expired) accent = 'from-rose-500 to-red-600 shadow-rose-500/25';
                    else if (expiringSoon) accent = 'from-amber-500 to-orange-500 shadow-amber-500/25';

                    const cap = c.maxUsesTotal != null && c.maxUsesTotal > 0 ? c.maxUsesTotal : null;
                    const pct = cap ? Math.min(100, Math.round((c.usesCount / cap) * 100)) : c.usesCount > 0 ? 100 : 0;

                    return (
                      <div
                        key={c.id}
                        className="group relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_8px_30px_-16px_rgba(15,23,42,0.35)] transition hover:border-indigo-200/80 hover:shadow-[0_16px_40px_-18px_rgba(67,56,202,0.22)]"
                      >
                        <div
                          className={`absolute right-0 top-0 h-full w-1.5 bg-gradient-to-b ${accent}`}
                          aria-hidden
                        />
                        <div className="flex flex-col gap-4 p-4 pr-6 sm:flex-row sm:items-stretch sm:justify-between sm:gap-6">
                          <div className="min-w-0 flex-1 space-y-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="font-mono text-lg font-black tracking-wide text-slate-900" dir="ltr">
                                    {c.code}
                                  </p>
                                  <button
                                    type="button"
                                    title="کپی کد"
                                    onClick={() => void copyCouponCode(c.code, c.id)}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </button>
                                  {copiedCouponId === c.id ? (
                                    <span className="text-[11px] font-bold text-emerald-600">کپی شد</span>
                                  ) : null}
                                </div>
                                {c.description ? (
                                  <p className="mt-1 line-clamp-2 text-[13px] font-medium leading-relaxed text-slate-600">
                                    {c.description}
                                  </p>
                                ) : null}
                              </div>
                              <button
                                type="button"
                                onClick={() => void handleToggleActive(c)}
                                className={`shrink-0 rounded-xl px-3 py-2 text-xs font-black transition sm:py-2.5 sm:text-sm ${
                                  c.isActive
                                    ? 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200 hover:bg-emerald-200'
                                    : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200 hover:bg-slate-200'
                                }`}
                              >
                                {c.isActive ? 'فعال' : 'غیرفعال'}
                              </button>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              {c.campaignLabel ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 text-[11px] font-black text-violet-900 ring-1 ring-violet-100">
                                  <Sparkles className="h-3 w-3" />
                                  {c.campaignLabel}
                                </span>
                              ) : (
                                <span className="text-[11px] font-bold text-slate-400">بدون برچسب کمپین</span>
                              )}
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-black ring-1 ${
                                  c.discountKind === 'PERCENT'
                                    ? 'bg-sky-50 text-sky-900 ring-sky-100'
                                    : 'bg-fuchsia-50 text-fuchsia-900 ring-fuchsia-100'
                                }`}
                              >
                                {c.discountKind === 'PERCENT' ? 'درصدی' : 'مبلغ ثابت'}
                              </span>
                              {couponHasAdvancedRules(c) ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-black text-amber-900 ring-1 ring-amber-100">
                                  <TrendingUp className="h-3 w-3" />
                                  قواعد پیشرفته
                                </span>
                              ) : null}
                              {expired ? (
                                <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-black text-rose-800 ring-1 ring-rose-100">
                                  منقضی
                                </span>
                              ) : null}
                              {!expired && expiringSoon && c.isActive ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-black text-orange-900 ring-1 ring-orange-100">
                                  <Clock className="h-3 w-3" />
                                  نزدیک انقضا
                                </span>
                              ) : null}
                            </div>

                            <div className="flex flex-wrap gap-2 text-[11px] font-semibold leading-relaxed text-slate-600">
                              {(c.applicableCourseIds ?? []).length > 0 ? (
                                <span className="rounded-lg bg-slate-50 px-2 py-1 ring-1 ring-slate-100">
                                  {(c.applicableCourseIds ?? []).length} دوره مشمول
                                </span>
                              ) : (
                                <span className="rounded-lg bg-slate-50 px-2 py-1 ring-1 ring-slate-100">همه دوره‌ها</span>
                              )}
                              {(c.excludedCourseIds ?? []).length > 0 ? (
                                <span className="rounded-lg bg-slate-50 px-2 py-1 ring-1 ring-slate-100">
                                  {(c.excludedCourseIds ?? []).length} مستثنی
                                </span>
                              ) : null}
                              {c.firstPaidPurchaseOnly ? (
                                <span className="rounded-lg bg-slate-50 px-2 py-1 ring-1 ring-slate-100">
                                  اولین خرید پولی
                                </span>
                              ) : null}
                              {c.newUsersRegisteredAfter ? (
                                <span className="rounded-lg bg-slate-50 px-2 py-1 ring-1 ring-slate-100">
                                  کاربر جدید بعد از تاریخ
                                </span>
                              ) : null}
                            </div>

                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                              <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2">
                                <p className="text-[10px] font-bold text-slate-500">مبلغ تخفیف</p>
                                <p className="mt-1 text-base font-black text-indigo-900">
                                  {c.discountKind === 'PERCENT'
                                    ? `${formatNum(toNum(c.percentOff))}٪`
                                    : `${formatNum(toNum(c.amountOffToman))} ت`}
                                </p>
                              </div>
                              <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2">
                                <p className="text-[10px] font-bold text-slate-500">مصرف / سقف</p>
                                <p className="mt-1 font-mono text-sm font-black text-slate-900">
                                  {formatNum(c.usesCount)}
                                  {cap != null ? ` / ${formatNum(cap)}` : ''}
                                </p>
                                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-200">
                                  <div
                                    className="h-full rounded-full bg-gradient-to-l from-indigo-600 to-violet-500 transition-all duration-300"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                              <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2">
                                <p className="text-[10px] font-bold text-slate-500">شروع معتبر</p>
                                <p className="mt-1 text-[11px] font-bold leading-snug text-slate-800">
                                  {startMs ? new Date(c.startsAt!).toLocaleDateString('fa-IR') : '— بدون محدودیت —'}
                                </p>
                              </div>
                              <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2">
                                <p className="text-[10px] font-bold text-slate-500">پایان معتبر</p>
                                <p className="mt-1 text-[11px] font-bold leading-snug text-slate-800">
                                  {endMs ? new Date(c.endsAt!).toLocaleDateString('fa-IR') : '— بدون محدودیت —'}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="flex shrink-0 flex-col justify-between gap-2 border-t border-slate-100 pt-3 sm:border-t-0 sm:border-r sm:pt-0 sm:pr-4">
                            <p className="text-[10px] font-bold text-slate-400">عملیات</p>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => void handleDuplicate(c)}
                                className="inline-flex items-center justify-center gap-1 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] font-black text-violet-900 hover:bg-violet-100"
                              >
                                <Copy className="h-3.5 w-3.5 opacity-70" />
                                کپی طرح
                              </button>
                              <button
                                type="button"
                                onClick={() => void openRedemptions(c)}
                                className="inline-flex items-center justify-center gap-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-black text-slate-800 hover:bg-slate-100"
                              >
                                <ClipboardList className="h-3.5 w-3.5 opacity-70" />
                                گزارش استفاده
                              </button>
                              <button
                                type="button"
                                onClick={() => openEdit(c)}
                                className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-3 py-2 text-[11px] font-black text-white hover:bg-indigo-700"
                              >
                                ویرایش
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleDelete(c)}
                                className="inline-flex items-center justify-center rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-black text-red-700 hover:bg-red-100"
                              >
                                حذف
                              </button>
                            </div>
                            <p className="hidden text-[10px] font-medium text-slate-400 sm:block">
                              به‌روز {new Date(c.updatedAt).toLocaleString('fa-IR')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm text-gray-600">
                <span className="font-semibold">
                  صفحه {listPageSafe.toLocaleString('fa-IR')} از {listTotalPages.toLocaleString('fa-IR')} — نمایش{' '}
                  {paginatedRows.length.toLocaleString('fa-IR')} از {filteredSortedRows.length.toLocaleString('fa-IR')}{' '}
                  کوپن
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={listPageSafe <= 1}
                    onClick={() => setListPage((p) => Math.max(1, p - 1))}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold disabled:opacity-40"
                  >
                    قبلی
                  </button>
                  <button
                    type="button"
                    disabled={listPageSafe >= listTotalPages}
                    onClick={() => setListPage((p) => Math.min(listTotalPages, p + 1))}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-bold disabled:opacity-40"
                  >
                    بعدی
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        title={editing ? 'ویرایش کوپن' : 'ایجاد کوپن حرفه‌ای'}
        size="large"
        onClose={() => {
          if (!submitting) setModalOpen(false);
        }}
      >
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3 text-sm max-h-[76vh] overflow-y-auto px-1">
          {coursesLoading ? (
            <div className="rounded-xl border border-amber-100 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900">
              در حال بارگذاری فهرست دوره‌ها برای انتخاب…
            </div>
          ) : null}

          <SectionCard icon={Ticket} title="شناسه و برچسب">
            <label className="block">
              <span className="text-xs font-bold text-slate-600">کد کوپن (لاتین؛ یکتا)</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-mono"
                dir="ltr"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                required
                disabled={!!editing}
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">توضیح داخلی</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-600">
                <Tags className="h-3.5 w-3.5" />
                برچسب کمپین (گزارش و دسته‌بندی)
              </span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                placeholder="مثلاً کمپین_بهار_۱۴۰۴"
                maxLength={160}
                value={form.campaignLabel}
                onChange={(e) => setForm((f) => ({ ...f, campaignLabel: e.target.value }))}
              />
            </label>
          </SectionCard>

          <SectionCard icon={Percent} title="نوع تخفیف و مبلغ">
            <label className="block">
              <span className="text-xs font-bold text-slate-600">نوع</span>
              <select
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={form.discountKind}
                onChange={(e) =>
                  setForm((f) => ({ ...f, discountKind: e.target.value as FormState['discountKind'] }))
                }
              >
                <option value="PERCENT">درصد</option>
                <option value="FIXED_TOMAN">مبلغ ثابت (تومان)</option>
              </select>
            </label>
            {form.discountKind === 'PERCENT' ? (
              <>
                <label className="block">
                  <span className="text-xs font-bold text-slate-600">درصد</span>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                    value={form.percentOff}
                    onChange={(e) => setForm((f) => ({ ...f, percentOff: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-bold text-slate-600">سقف مبلغ تخفیف — تومان (اختیاری)</span>
                  <input
                    type="number"
                    min={0}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                    value={form.maxDiscountToman}
                    onChange={(e) => setForm((f) => ({ ...f, maxDiscountToman: e.target.value }))}
                    placeholder="حداکثر مبلغی که از هر خط سبد کسر می‌شود"
                  />
                </label>
              </>
            ) : (
              <label className="block">
                <span className="text-xs font-bold text-slate-600">مبلغ تخفیف (تومان)</span>
                <input
                  type="number"
                  min={0}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  value={form.amountOffToman}
                  onChange={(e) => setForm((f) => ({ ...f, amountOffToman: e.target.value }))}
                />
              </label>
            )}
          </SectionCard>

          <SectionCard icon={CalendarRange} title="زمان‌بندی و سقف استفاده">
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-xs font-bold text-slate-600">شروع اعتبار (اختیاری)</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  dir="ltr"
                  value={form.startsAt}
                  onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-slate-600">پایان اعتبار (اختیاری)</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  dir="ltr"
                  value={form.endsAt}
                  onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
                />
              </label>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50/70 px-3 py-2 space-y-1">
              <p className="text-[11px] font-bold leading-relaxed text-amber-950">
                سقف «هر کاربر» دو لایه دارد:
              </p>
              <ul className="list-disc pr-4 text-[11px] text-amber-900/90 leading-relaxed space-y-1">
                <li>
                  <strong>استفادهٔ کل با این کوپن (روی همهٔ دوره‌ها)</strong>: فیلد پایین. برای «هر نفر فقط یک
                  بار» عدد{' '}
                  <span dir="ltr" className="font-mono font-bold">
                    1
                  </span>{' '}
                  بگذارید. خالی = همان کاربر می‌تواند روی هر دورهٔ جدا (تا سقف فیلد دوم) دوباره تخفیف بگیرد.
                </li>
                <li>
                  <strong>روی همان دوره تکراری</strong>: فیلد «برای هر دوره». عدد{' '}
                  <span dir="ltr" className="font-mono font-bold">
                    1
                  </span>{' '}
                  یعنی روی هر دوره حداکثر یک بار؛ اگر سقف کل را خالی کرده باشید، باز هم می‌توانند با همین کد برای
                  دورهٔ دیگر خرید بزنند.
                </li>
              </ul>
            </div>
            <label className="block">
              <span className="text-xs font-black text-slate-800">
                حداکثر بار استفادهٔ هر کاربر با این کوپن (مجموع همهٔ دوره‌ها)
              </span>
              <input
                type="number"
                min={1}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={form.maxUsesPerUser}
                onChange={(e) => setForm((f) => ({ ...f, maxUsesPerUser: e.target.value }))}
                placeholder="برای کمپین یک‌بار مصرف: ۱؛ نامحدود روی چند دوره: خالی"
              />
              <span className="mt-1 block text-[11px] text-slate-500 leading-relaxed">
                پیش‌فرض کوپن تازه: ۱. اگر این فیلد را خالی کنید یعنی سقفی برای «چند بار در مجموع با این کد»
                ندارید؛ فقط محدودیت «بر هر دوره» اعمال می‌شود.
              </span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-xs font-bold text-slate-600">حداکثر کل استفادهٔ سیستم (خالی = نامحدود)</span>
                <input
                  type="number"
                  min={1}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  value={form.maxUsesTotal}
                  onChange={(e) => setForm((f) => ({ ...f, maxUsesTotal: e.target.value }))}
                  placeholder="مثلاً ۵۰۰ بار برای کل کمپین"
                />
              </label>
              <label className="block">
                <span className="text-xs font-bold text-slate-600">حداکثر برای همان دوره (Per user per course)</span>
                <input
                  type="number"
                  min={1}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  value={form.perUserPerCourseLimit}
                  onChange={(e) => setForm((f) => ({ ...f, perUserPerCourseLimit: e.target.value }))}
                  placeholder="معمولاً ۱"
                />
              </label>
            </div>
          </SectionCard>

          <SectionCard icon={Layers} title="محدوده دوره‌ها (شناسه و لیست)">
            <CourseIdListPicker
              title="فقط مشمول این دوره‌ها (لیست؛ خالی = همهٔ دوره‌های مجاز قبلی سیستم)"
              hint="شناسه CUID هر دوره. اگر خالی باشد روی هر دوره‌ای که بقیه شرایط را داشته باشد اعمال می‌شود؛ در غیر این صورت فقط روی خطوط سبد که در این مجموعه اند."
              listId="coupon-course-datalist-allow"
              selectedIds={form.applicableIds}
              counterpartIds={form.excludedIds}
              courses={coursesCatalog}
              onChange={(next) =>
                setForm((f) => ({
                  ...f,
                  applicableIds: next,
                  excludedIds: f.excludedIds.filter((x) => !next.includes(x)),
                }))
              }
            />
            <CourseIdListPicker
              title="دوره‌های مستثنی — هرگز تخفیف نمی‌گیرند حتی داخل سبدهای بلند."
              hint="اولویت بالاتر از «مشمول». روی هر آیتمی که با این لیست هم‌پوشانی داشت، خط سبد کنار می‌ماند؛ در کنار قواعد سبدهای چندگانه."
              listId="coupon-course-datalist-exclude"
              selectedIds={form.excludedIds}
              counterpartIds={form.applicableIds}
              courses={coursesCatalog}
              onChange={(next) =>
                setForm((f) => ({
                  ...f,
                  excludedIds: next,
                  applicableIds: f.applicableIds.filter((x) => !next.includes(x)),
                }))
              }
            />
          </SectionCard>

          <SectionCard icon={ShoppingCart} title="قواعد تک‌خط و سبد چند‌دوره‌ای">
            <label className="block">
              <span className="text-xs font-bold text-slate-600">حداقل مبلغ همان خط سفارش (تومان، اختیاری)</span>
              <input
                type="number"
                min={0}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                placeholder="به قیمت مؤثر همان دوره در سرور اشاره می‌کند."
                value={form.minOrderToman}
                onChange={(e) => setForm((f) => ({ ...f, minOrderToman: e.target.value }))}
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="inline-flex gap-1 items-center text-xs font-bold text-slate-600">
                  <ListOrdered className="h-3.5 w-3.5" />
                  حداقل تعداد خط در سبد
                </span>
                <input
                  type="number"
                  min={1}
                  placeholder="برای سبده چند دوره‌ای"
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  value={form.minCartLinesCount}
                  onChange={(e) => setForm((f) => ({ ...f, minCartLinesCount: e.target.value }))}
                />
                <span className="mt-1 block text-[11px] text-slate-500 leading-relaxed">
                  فقط هنگامی که سایت شناسهٔ چند دوره را برای سبد بفرستد اعمال می‌شود؛ پیش‌نمایش تک‌صفحهٔ دوره بدون لیست سبد این شرط را اغلب عدد صفر می‌بیند.
                </span>
              </label>
              <label className="block">
                <span className="text-xs font-bold text-slate-600">حداقل جمع قیمت مؤثر سبد (تومان)</span>
                <input
                  type="number"
                  min={0}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                  placeholder="خطوطی که شامل می‌شوند"
                  value={form.minCartSubtotalToman}
                  onChange={(e) => setForm((f) => ({ ...f, minCartSubtotalToman: e.target.value }))}
                />
              </label>
            </div>
          </SectionCard>

          <SectionCard icon={UserPlus} title="هدف کاربری پیشرفته">
            <label className="block">
              <span className="text-xs font-bold text-slate-600">مرز تاریخ ثبت‌نام — فقط کاربرانی که بعد از این لحظه ثبت شده‌اند</span>
              <input
                type="datetime-local"
                dir="ltr"
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
                value={form.newUsersRegisteredAfter}
                onChange={(e) => setForm((f) => ({ ...f, newUsersRegisteredAfter: e.target.value }))}
              />
              <span className="mt-1 block text-[11px] text-slate-500">
                تاریخ ایجاد حساب کاربر در سرور؛ برای کمپین «فقط مهاجمان تازه».
              </span>
            </label>
            <label className="inline-flex items-start gap-2 cursor-pointer mt-2">
              <input
                type="checkbox"
                checked={form.firstPaidPurchaseOnly}
                onChange={(e) => setForm((f) => ({ ...f, firstPaidPurchaseOnly: e.target.checked }))}
                className="mt-0.5"
              />
              <span className="text-xs font-bold text-slate-700 leading-relaxed">
                فقط اولین خرید پولی دورهٔ زندگی کاربر؛ اگر قبلاً فاکتور پرداخت‌شده با مبلغ مثبت داشته، کوپن رد می‌شود.
              </span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer mt-2">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              <span className="text-xs font-bold text-slate-700">کوپن فعال در لحظه</span>
            </label>
          </SectionCard>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 sticky bottom-0 bg-white pb-1">
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-4 py-2 font-semibold"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              انصراف
            </button>
            <button
              type="submit"
              disabled={submitting || coursesLoading}
              className="rounded-lg bg-blue-600 px-4 py-2 font-bold text-white disabled:opacity-50"
            >
              {submitting ? 'در حال ذخیره…' : 'ذخیره کوپن'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!redemptionsCoupon}
        title={redemptionsCoupon ? `گزارش استفاده از کوپن · ${redemptionsCoupon.code}` : ''}
        size="large"
        onClose={() => {
          setRedemptionsCoupon(null);
          setRedemptionRows([]);
        }}
      >
        {redemptionLoading ? (
          <div className="py-12 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : redemptionRows.length === 0 ? (
          <p className="text-sm text-slate-600 py-4">هنوز استفاده‌ای از این کوپن ثبت نشده است.</p>
        ) : (
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-slate-500 leading-relaxed">
              فهرست کاربرانی که با این کد تخفیف خرید ثبت کرده‌اند؛ شامل مشخصات حساب، دوره، فاکتور و مبالغ درج‌شده در سیستم.
              {redemptionRows.length >= 3000 ? (
                <span className="block mt-1 text-amber-800 font-black">
                  تنها ۳۰۰۰ ردیف اخیر نمایش داده می‌شود؛ در صورت نیاز به خروجی کامل از دیتابیس بپرسید.
                </span>
              ) : null}
            </p>
            <div className="overflow-x-auto max-h-[65vh] rounded-xl border border-slate-200">
              <table className="min-w-[1180px] w-full text-[11px]">
                <thead className="bg-slate-800 text-[10px] font-black text-white sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">زمان استفاده</th>
                    <th className="px-2.5 py-2.5 text-right">نام</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">نام کاربری</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">موبایل</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">ایمیل</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">عضویت کاربر</th>
                    <th className="px-2.5 py-2.5 text-right min-w-[140px]">دوره</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">شماره فاکتور</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">نوع</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">کد در فاکتور</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">وضعیت</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">مبلغ فاکتور</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">جمع قبل کوپن</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">تخفیف (فاکتور)</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">تخفیف ثبت‌شده</th>
                    <th className="px-2.5 py-2.5 text-right whitespace-nowrap">زمان پرداخت</th>
                    <th className="px-2.5 py-2.5 text-right font-mono text-[9px]" dir="ltr">
                      شناسه کاربر
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {redemptionRows.map((r) => {
                    const inv = r.invoice;
                    const subPre = inv?.courseSubtotalToman != null ? toNum(inv.courseSubtotalToman) : NaN;
                    const discInv = inv?.couponDiscountToman != null ? toNum(inv.couponDiscountToman) : NaN;
                    const paidAt =
                      inv?.paidAt &&
                      Number.isFinite(new Date(inv.paidAt).getTime())
                        ? new Date(inv.paidAt!).toLocaleString('fa-IR')
                        : '—';
                    const userJoined =
                      r.user?.createdAt && Number.isFinite(new Date(r.user.createdAt).getTime())
                        ? new Date(r.user.createdAt).toLocaleDateString('fa-IR')
                        : '—';
                    return (
                      <tr key={r.id} className="border-t border-slate-100 bg-white hover:bg-slate-50/80">
                        <td className="px-2.5 py-2 whitespace-nowrap font-semibold text-slate-800">
                          {new Date(r.createdAt).toLocaleString('fa-IR')}
                        </td>
                        <td className="px-2.5 py-2 text-slate-900 font-semibold">{userDisplayName(r.user)}</td>
                        <td className="px-2.5 py-2 font-mono text-[10px]" dir="ltr">
                          {r.user?.username ?? '—'}
                        </td>
                        <td className="px-2.5 py-2 font-mono whitespace-nowrap" dir="ltr">
                          {r.user?.phone ?? '—'}
                        </td>
                        <td className="px-2.5 py-2 font-mono text-[10px] break-all" dir="ltr">
                          {r.user?.email ?? '—'}
                        </td>
                        <td className="px-2.5 py-2 whitespace-nowrap text-slate-700">{userJoined}</td>
                        <td className="px-2.5 py-2 leading-snug text-slate-800">
                          <span className="block font-bold">{r.course?.title ?? '—'}</span>
                          {r.course?.slug ? (
                            <span className="block mt-0.5 text-[10px] text-slate-500 font-mono" dir="ltr">
                              /{r.course.slug}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-2.5 py-2 font-mono font-bold whitespace-nowrap" dir="ltr">
                          {inv?.invoiceNumber ?? '—'}
                        </td>
                        <td className="px-2.5 py-2 whitespace-nowrap text-slate-700">
                          {inv ? formatInvoiceType(inv.type) : '—'}
                        </td>
                        <td className="px-2.5 py-2 font-mono text-[10px] font-bold whitespace-nowrap" dir="ltr">
                          {inv?.couponCodeSnapshot ?? '—'}
                        </td>
                        <td className="px-2.5 py-2 whitespace-nowrap">
                          {inv ? (
                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-black text-slate-800">
                              {formatPaymentStatus(inv.status)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-2.5 py-2 whitespace-nowrap font-mono tabular-nums font-bold">
                          {inv ? `${formatNum(toNum(inv.amount))} ت` : '—'}
                        </td>
                        <td className="px-2.5 py-2 whitespace-nowrap font-mono tabular-nums">
                          {!Number.isNaN(subPre) ? `${formatNum(subPre)} ت` : '—'}
                        </td>
                        <td className="px-2.5 py-2 whitespace-nowrap font-mono tabular-nums text-emerald-800 font-bold">
                          {!Number.isNaN(discInv) ? `${formatNum(discInv)} ت` : '—'}
                        </td>
                        <td className="px-2.5 py-2 whitespace-nowrap font-mono tabular-nums font-bold text-indigo-800">
                          {formatNum(toNum(r.discountToman))} ت
                        </td>
                        <td className="px-2.5 py-2 whitespace-nowrap text-slate-700">{paidAt}</td>
                        <td className="px-2.5 py-2 font-mono text-[10px] text-slate-500" dir="ltr">
                          {r.userId}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Coupons;
